import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Tutorial = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeRole, setActiveRole] = useState(user?.role || 'executor');

  const roleGuides = {
    executor: {
      title: 'Executor Guide',
      icon: '👤',
      color: 'bg-blue-500',
      description: 'Panduan untuk membuat dan menghantar storyboard content',
      steps: [
        {
          title: '1. Dashboard Overview',
          content: 'Selepas login, anda akan melihat Dashboard dengan statistik submissions anda. Panel menunjukkan Total Submissions, Approved, Pending, dan lain-lain.',
          action: 'Klik pada mana-mana panel untuk melihat senarai penuh.'
        },
        {
          title: '2. Masuk ke Content Studio',
          content: 'Dari Dashboard, klik "Studios" di navigation bar bawah, kemudian pilih "Content Studio". Ini adalah tempat utama untuk membuat storyboard.',
          action: 'Studios → Content Studio'
        },
        {
          title: '3. SOA Drill - Latihan AI',
          content: 'Sebelum buat storyboard, anda perlu lulus SOA Drill. Ini adalah latihan untuk memahami product knowledge. AI Coach akan membantu anda.',
          tips: [
            'Jawab soalan dengan teliti',
            'Gunakan "AI Rescue" jika stuck',
            'Minimum 3 attempts diperlukan'
          ]
        },
        {
          title: '4. Generate Storyboard',
          content: 'Selepas lulus SOA Drill, klik "Generate Storyboard". AI akan create storyboard berdasarkan jawapan anda dalam drill.',
          action: 'Review setiap beat dan pastikan content sesuai.'
        },
        {
          title: '5. Review & Prime',
          content: 'Sebelum submit, review storyboard anda. Gunakan "Review & Prime" untuk fine-tune jika perlu.',
          action: 'Klik "Submit for Approval" apabila siap.'
        },
        {
          title: '6. Track Submissions',
          content: 'Pergi ke "My Work" di navigation bar untuk track status semua submissions anda.',
          statuses: [
            { status: 'Pending', desc: 'Menunggu supervisor review' },
            { status: 'Approved', desc: 'Storyboard diluluskan' },
            { status: 'Changes Requested', desc: 'Perlu edit dan resubmit' }
          ]
        }
      ]
    },
    supervisor: {
      title: 'Supervisor Guide',
      icon: '👨‍💼',
      color: 'bg-green-500',
      description: 'Panduan untuk review dan approve submissions dari executor',
      steps: [
        {
          title: '1. Dashboard Overview',
          content: 'Supervisor Dashboard menunjukkan pending items yang perlu diproses. Panel "Pending Storyboard" dan "Videos to Review" adalah priority utama.',
          action: 'Klik panel untuk melihat queue.'
        },
        {
          title: '2. Review Queue',
          content: 'Klik "Pending Storyboard" untuk masuk ke Queue page. Di sini anda boleh lihat semua submissions yang menunggu approval.',
          action: 'Setiap item ada butang View, Approve, Request Edit, dan Reject.'
        },
        {
          title: '3. Review Storyboard',
          content: 'Klik "View" untuk review storyboard penuh. Check setiap beat untuk content quality dan compliance.',
          checkList: [
            'Product information tepat',
            'Visual descriptions sesuai',
            'Audio/script berkualiti',
            'Compliance requirements dipenuhi'
          ]
        },
        {
          title: '4. Take Action',
          content: 'Selepas review, pilih action yang sesuai:',
          actions: [
            { action: '✓ Approve', desc: 'Luluskan storyboard untuk production' },
            { action: '✏️ Request Edit', desc: 'Minta executor buat perubahan' },
            { action: '✗ Reject', desc: 'Reject sepenuhnya (jarang digunakan)' }
          ]
        },
        {
          title: '5. Video Review',
          content: 'Selepas storyboard diluluskan, executor akan submit video. Review video di "Videos to Review" panel.',
          action: 'Watch video dan approve/reject.'
        },
        {
          title: '6. Monitor Team Performance',
          content: 'Gunakan "Executor Performance Tracker" di Dashboard untuk monitor team anda.',
          metrics: ['Submissions count', 'Approval rate', 'Average turnaround time']
        }
      ]
    },
    management: {
      title: 'Management Guide',
      icon: '📊',
      color: 'bg-purple-500',
      description: 'Panduan untuk analytics, knowledge packs, dan system oversight',
      steps: [
        {
          title: '1. Analytics Dashboard',
          content: 'Akses Analytics dari menu untuk melihat performance metrics across teams.',
          action: 'Navigate to Analytics → View detailed reports'
        },
        {
          title: '2. Knowledge Pack Management',
          content: 'Manage product knowledge packs yang digunakan oleh AI system.',
          action: 'Knowledge Packs → Create/Edit packs'
        },
        {
          title: '3. Campaign Management',
          content: 'Setup dan manage campaigns untuk content production.',
          action: 'Campaigns → Create new campaign'
        },
        {
          title: '4. Team Oversight',
          content: 'Monitor all teams performance dan compliance metrics.',
          action: 'Analytics → Team Performance'
        },
        {
          title: '5. Approval Oversight',
          content: 'Management juga boleh approve submissions jika diperlukan.',
          action: 'Queue → Review pending items'
        }
      ]
    },
    admin: {
      title: 'Admin Guide',
      icon: '⚙️',
      color: 'bg-red-500',
      description: 'Panduan untuk system administration dan user management',
      steps: [
        {
          title: '1. Admin Dashboard',
          content: 'Access Admin panel dari menu untuk full system control.',
          action: 'Admin → Dashboard'
        },
        {
          title: '2. User Management',
          content: 'Add, edit, atau remove users. Assign roles dan functional groups.',
          action: 'Admin → Users → Manage'
        },
        {
          title: '3. Functional Teams',
          content: 'Setup functional groups/teams untuk organize users.',
          action: 'Admin → Functional Teams'
        },
        {
          title: '4. System Integrations',
          content: 'Configure third-party integrations seperti WhatsApp, Email, dll.',
          action: 'Admin → Integrations'
        },
        {
          title: '5. AI Model Configuration',
          content: 'Configure AI model settings dan parameters.',
          action: 'Admin → AI Model'
        },
        {
          title: '6. Backup & Maintenance',
          content: 'Run backups dan system maintenance tasks.',
          action: 'Admin → Backup'
        },
        {
          title: '7. Data Reset (Deployment)',
          content: 'Untuk deployment, admin boleh reset semua data sambil keep user accounts.',
          warning: 'Ini akan padam semua submissions, sessions, dan activity data!'
        }
      ]
    }
  };

  const roles = [
    { key: 'executor', label: 'Executor', icon: '👤' },
    { key: 'supervisor', label: 'Supervisor', icon: '👨‍💼' },
    { key: 'management', label: 'Management', icon: '📊' },
    { key: 'admin', label: 'Admin', icon: '⚙️' }
  ];

  const currentGuide = roleGuides[activeRole] || roleGuides.executor;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-emzi-blue text-white px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-white hover:bg-white/20"
            >
              ← Back
            </Button>
            <div>
              <h1 className="text-xl font-bold">📚 EMZI NEXUS Tutorial</h1>
              <p className="text-sm opacity-90">Panduan Pengguna</p>
            </div>
          </div>
          <Badge className="bg-white text-emzi-blue">
            v6.4
          </Badge>
        </div>
      </div>

      {/* Role Selector */}
      <div className="px-4 py-3 bg-white border-b">
        <p className="text-xs text-gray-500 mb-2">Pilih Role:</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {roles.map((role) => (
            <Button
              key={role.key}
              size="sm"
              variant={activeRole === role.key ? 'default' : 'outline'}
              onClick={() => setActiveRole(role.key)}
              className={`whitespace-nowrap ${
                activeRole === role.key ? 'bg-emzi-blue' : ''
              }`}
            >
              {role.icon} {role.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Guide Content */}
      <div className="p-4 space-y-4 pb-24">
        {/* Role Header */}
        <Card className={`${currentGuide.color} text-white border-0`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{currentGuide.icon}</span>
              <div>
                <h2 className="text-xl font-bold">{currentGuide.title}</h2>
                <p className="text-sm opacity-90">{currentGuide.description}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Steps */}
        {currentGuide.steps.map((step, index) => (
          <Card key={`item-${index}`} className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-bold text-gray-800">
                {step.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-gray-600">{step.content}</p>
              
              {step.action && (
                <div className="bg-blue-50 p-2 rounded-lg">
                  <p className="text-xs font-medium text-blue-700">💡 Action: {step.action}</p>
                </div>
              )}

              {step.tips && (
                <div className="bg-yellow-50 p-2 rounded-lg">
                  <p className="text-xs font-bold text-yellow-700 mb-1">💡 Tips:</p>
                  <ul className="text-xs text-yellow-700 space-y-1">
                    {step.tips.map((tip, i) => (
                      <li key={`item-${i}`}>• {tip}</li>
                    ))}
                  </ul>
                </div>
              )}

              {step.statuses && (
                <div className="space-y-1">
                  {step.statuses.map((s, i) => (
                    <div key={`item-${i}`} className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className="text-xs">{s.status}</Badge>
                      <span className="text-gray-600">{s.desc}</span>
                    </div>
                  ))}
                </div>
              )}

              {step.checkList && (
                <div className="bg-green-50 p-2 rounded-lg">
                  <p className="text-xs font-bold text-green-700 mb-1">✅ Checklist:</p>
                  <ul className="text-xs text-green-700 space-y-1">
                    {step.checkList.map((item, i) => (
                      <li key={`item-${i}`}>☐ {item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {step.actions && (
                <div className="space-y-1">
                  {step.actions.map((a, i) => (
                    <div key={`item-${i}`} className="flex items-start gap-2 text-xs bg-gray-50 p-2 rounded">
                      <span className="font-bold">{a.action}</span>
                      <span className="text-gray-600">- {a.desc}</span>
                    </div>
                  ))}
                </div>
              )}

              {step.metrics && (
                <div className="flex flex-wrap gap-1">
                  {step.metrics.map((m, i) => (
                    <Badge key={`item-${i}`} variant="outline" className="text-xs">{m}</Badge>
                  ))}
                </div>
              )}

              {step.warning && (
                <div className="bg-red-50 p-2 rounded-lg border border-red-200">
                  <p className="text-xs font-bold text-red-700">⚠️ Warning: {step.warning}</p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}

        {/* Quick Tips */}
        <Card className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0">
          <CardContent className="p-4">
            <h3 className="font-bold mb-2">🚀 Quick Tips</h3>
            <ul className="text-sm space-y-1 opacity-90">
              <li>• Gunakan navigation bar di bawah untuk akses cepat</li>
              <li>• Profile boleh diakses dari icon di header</li>
              <li>• Refresh page jika data tidak sync</li>
              <li>• Contact admin jika ada masalah teknikal</li>
            </ul>
          </CardContent>
        </Card>

        {/* Support */}
        <Card className="border-0 shadow-md">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-gray-600 mb-2">Perlukan bantuan lanjut?</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/communications')}
            >
              📧 Contact Support
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Tutorial;
